import {
    TranscribeStreamingClient,
    StartStreamTranscriptionCommand,
} from "@aws-sdk/client-transcribe-streaming";
import MicrophoneStream from "microphone-stream";
import { Buffer } from "buffer";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import * as awsID from "./awsID.js";
import { notification } from "antd";

const credentials = {
    accessKeyId: "AKIA5XAQ26UWRZCW5Q7Q",
    secretAccessKey: "lYoZVf6tzs2TJjH2V/C3/DjbTeXLk57qE3CrEmW0",
};
const SAMPLE_RATE = 44100;
const SILENCE_THRESHOLD = 2000; 
const SPEECH_TIMEOUT = 1000; 

let microphoneStream;
let transcribeClient;
let shouldStop = false;
let silenceTimer = null;
let lastSpeechTime = Date.now();
let accumulatedTranscription = "";
let isAccumulating = false;

const checkMicrophoneStatus = async () => {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        return true; 
    } catch (error) {
        return false; 
    }
};

const showMicrophoneAlert = async () => {
    const isMicrophoneOn = await checkMicrophoneStatus();
    if (!isMicrophoneOn) {
        notification.error({ 
            message: 'Your microphone is off or not accessible. Please check your microphone settings.',
            duration: 10 
        });
    }
};

export const startRecording = async (language, callback, onStartCallback) => {
    if (!language) return false;
    const isMicrophoneOn = await checkMicrophoneStatus();
    if (!isMicrophoneOn) {
        alert("Your microphone is off or not accessible. Please check your microphone settings.");
        return false;
    }
    shouldStop = false;
    accumulatedTranscription = "";
    isAccumulating = false;
    
    if (microphoneStream || transcribeClient) {
        await cleanupStreams();
    }
    
    await createTranscribeClient();
    await createMicrophoneStream();
    await startStreaming("en-US", callback, onStartCallback);
};

export const stopRecording = () => {
    shouldStop = true;
    if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
    }
    accumulatedTranscription = "";
    isAccumulating = false;
};

const createTranscribeClient = () => {
    transcribeClient = new TranscribeStreamingClient({
        region: awsID.REGION,
        credentials: fromCognitoIdentityPool({
            client: new CognitoIdentityClient({ region: awsID.REGION }),
            identityPoolId: awsID.IDENTITY_POOL_ID,
        }),
    });
};

const createMicrophoneStream = async () => {
    try {
        microphoneStream = new MicrophoneStream();
        microphoneStream.setStream(
            await navigator.mediaDevices.getUserMedia({
                audio: {
                    autoGainControl: true,
                    noiseSuppression: true,
                    echoCancellation: true,
                    sampleRate: 16000,
                    channelCount: 1
                },
                video: false
            })
        );
    } catch (error) {
        console.error("Error accessing the microphone:", error);
        alert("Error accessing the microphone. Please check your microphone settings.");
    }
};

const handleSpeechSegment = (callback, currentTranscription, isPartial) => {
    const now = Date.now();
    const timeSinceLastSpeech = now - lastSpeechTime;

    if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
    }

    lastSpeechTime = now;

    if (isPartial) {
        if (!isAccumulating && timeSinceLastSpeech < SPEECH_TIMEOUT) {
            isAccumulating = true;
        }

        if (isAccumulating) {
            callback({ 
                partial: currentTranscription
            });
        } else {
            callback({ partial: currentTranscription });
        }
    } else {
        if (isAccumulating) {
            accumulatedTranscription = (accumulatedTranscription + " " + currentTranscription).trim();
        } else {
            accumulatedTranscription = currentTranscription;
        }
    }

    silenceTimer = setTimeout(() => {
        if (Date.now() - lastSpeechTime >= SILENCE_THRESHOLD) {
            if (accumulatedTranscription) {
                callback({ final: accumulatedTranscription });
                accumulatedTranscription = "";
                isAccumulating = false;
                shouldStop = true;
                cleanupStreams();
            }
        }
    }, SILENCE_THRESHOLD);
};

const startStreaming = async (language, callback, onStartCallback) => {
    try {
        console.log("Starting streaming transcription...");
        const command = new StartStreamTranscriptionCommand({
            LanguageCode: language,
            MediaEncoding: "pcm",
            MediaSampleRateHertz: SAMPLE_RATE,
            AudioStream: getAudioStream(),
        });
        
        const data = await transcribeClient.send(command);
        console.log("SPEAK Now");
        if (onStartCallback) onStartCallback();

        for await (const event of data.TranscriptResultStream) {
            if (shouldStop) {
                cleanupStreams();
                return;
            }
            
            for (const result of event.TranscriptEvent.Transcript.Results || []) {
                if (!result.Alternatives?.[0]?.Items?.length) continue;
                
                let currentTranscription = result.Alternatives[0].Items
                    .map(item => item.Content)
                    .join(" ");

                handleSpeechSegment(callback, currentTranscription, result.IsPartial);
            }
        }
    } catch (e) {
        console.error("Transcription error:", e);
    }
};

export const cleanupStreams = () => {
    if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
    }
    
    accumulatedTranscription = "";
    isAccumulating = false;
    
    if (microphoneStream) {
        microphoneStream.stop();
        microphoneStream.destroy();
        microphoneStream = undefined;
    }
    
    if (transcribeClient) {
        transcribeClient.destroy();
        transcribeClient = undefined;
    }
    
    if (window.navigator && window.navigator.mediaDevices) {
        window.navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
                let tracks = stream.getTracks();
                tracks.forEach((track) => track.stop());
            })
            .catch((error) => console.error("Error releasing media devices:", error));
    }
    shouldStop = false;
};

const getAudioStream = async function* () {
    for await (const chunk of microphoneStream) {
        if (chunk.length <= SAMPLE_RATE) {
            yield {
                AudioEvent: {
                    AudioChunk: encodePCMChunk(chunk),
                },
            };
        }
    }
};

const encodePCMChunk = (chunk) => {
    const input = MicrophoneStream.toRaw(chunk);
    let offset = 0;
    const buffer = new ArrayBuffer(input.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < input.length; i++, offset += 2) {
        let s = Math.max(-1, Math.min(1, input[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return new Uint8Array(buffer);
};

export { showMicrophoneAlert, checkMicrophoneStatus };